import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import InputMask from "react-input-mask";
import Swal from "sweetalert2";

import { MainButton } from "../../components/MainButton";
import { Menu } from "../../components/Menu";
import { Header } from "../../components/Header";
import { Loading } from "../../components/Loading";
import { ConfigType } from "../../contexts/AppConfigContext";
import { useAppConfig } from "../../hooks/useAppConfig";
import { usePluginspace } from "../../hooks/usePluginspace";
import useAuth from "../../hooks/useAuth";
import api from "../../services/api";
import {
  cpfCnpjNoMask,
  datetimeToDateBR,
  datetimeToDateSendOrder,
} from "../../functions/mask";
import { floatToPriceWithoutSpace } from "../../functions/price_functions";
import {
  CashbackTransaction,
  CashbackTransactionType,
  getAvailableCashbackWallet,
  getCashbackTransactions,
} from "../NewCheckoutCart/factory/cashbackHelper";
import "./index.css";

const TYPE_LABEL: Record<CashbackTransactionType, string> = {
  earn: "Acúmulo",
  redeem: "Resgate",
  reversal: "Estorno",
};

function formatTransactionDate(value?: string) {
  if (!value) return "";
  try {
    const datePart = datetimeToDateBR(value);
    const timePart =
      value.length >= 16
        ? `${value.substring(11, 13)}:${value.substring(14, 16)}`
        : "";
    return timePart ? `${datePart} ${timePart}` : datePart;
  } catch {
    return value;
  }
}

function sameBirthDate(apiDate: string, inputDate: string) {
  const fromApi = datetimeToDateSendOrder(apiDate);
  const fromInput = datetimeToDateSendOrder(inputDate);
  return Boolean(fromApi && fromInput && fromApi === fromInput);
}

function formatSignedAmount(amount: number) {
  const value = Number(amount) || 0;
  const formatted = floatToPriceWithoutSpace(Math.abs(value));
  if (value < 0) return `- ${formatted}`;
  if (value > 0) return `+ ${formatted}`;
  return formatted;
}

export function CashbackStatement() {
  const navigate = useNavigate();
  const { getToken, registerAccess } = usePluginspace();
  const { loginInfo } = useAuth();
  const { getAppConfig } = useAppConfig();

  const [config, setConfig] = useState<ConfigType | void>();
  const [loadingHeader, setLoadingHeader] = useState(true);
  const [loading, setLoading] = useState(true);
  const [identifying, setIdentifying] = useState(false);

  const [cpf, setCpf] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [customerName, setCustomerName] = useState("");

  const [availableBalance, setAvailableBalance] = useState(0);
  const [pendingBalance, setPendingBalance] = useState(0);
  const [totalBalance, setTotalBalance] = useState(0);
  const [transactions, setTransactions] = useState<CashbackTransaction[]>([]);

  const isIdentified = customerId !== null;

  const accent = config?.webappcolor?.webappbutton || "#37393D";
  const accentFont = config?.webappcolor?.fontbutton || "#FFFFFF";

  const loadStatement = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        const token = await getToken();
        const [walletRes, transactionsRes] = await Promise.all([
          getAvailableCashbackWallet(id, api, token),
          getCashbackTransactions(id, api, token),
        ]);

        const available = Number(
          walletRes.data?.availableBalance ?? walletRes.data?.balance ?? 0,
        );
        const pending = Number(walletRes.data?.pendingBalance ?? 0);
        const balance = Number(walletRes.data?.balance ?? available + pending);

        setAvailableBalance(Number.isFinite(available) ? available : 0);
        setPendingBalance(Number.isFinite(pending) ? pending : 0);
        setTotalBalance(Number.isFinite(balance) ? balance : 0);

        if (!transactionsRes.success) {
          Swal.fire({
            icon: "warning",
            title: "Atenção!",
            text:
              transactionsRes.errorMsg ||
              "Não foi possível carregar o extrato de cashback.",
          });
          setTransactions([]);
        } else {
          setTransactions(transactionsRes.data || []);
        }
      } catch (error) {
        console.error(error);
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: "Não foi possível carregar o extrato de cashback.",
        });
      } finally {
        setLoading(false);
      }
    },
    [getToken],
  );

  useEffect(() => {
    registerAccess("cashback-statement");

    const start = async () => {
      const willAutoIdentify = Boolean(loginInfo?.identifier);
      setLoadingHeader(true);
      setConfig(await getAppConfig());
      setLoadingHeader(false);

      if (loginInfo?.identifier) {
        setCpf(loginInfo.identifier);
        if (!customerId) {
          await identifyByDocument(loginInfo.identifier, true);
        }
      } else if (!willAutoIdentify) {
        setLoading(false);
      }
    };

    void start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginInfo?.identifier]);

  async function identifyByDocument(
    documentValue: string,
    skipBirthDate = false,
  ) {
    const cleanCpf = cpfCnpjNoMask(documentValue);

    if (!cleanCpf || cleanCpf.length < 11) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: "Informe um CPF válido.",
      });
      return;
    }

    if (!skipBirthDate) {
      if (!birthDate || birthDate.includes("_") || birthDate.length < 10) {
        Swal.fire({
          icon: "warning",
          title: "Atenção!",
          text: "Informe a data de nascimento.",
        });
        return;
      }
    }

    setIdentifying(true);
    setLoading(true);

    try {
      const response = await api.post(
        `/api/customer/filter`,
        { cpf: cleanCpf },
        {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        },
      );

      const customer = response?.data?.data?.[0];

      if (!customer?.id) {
        Swal.fire({
          icon: "warning",
          title: "Atenção!",
          text: "Cliente não encontrado com os dados informados.",
        });
        setLoading(false);
        return;
      }

      if (
        (customer.customerStatus && customer.customerStatus === "Bloqueado") ||
        (customer.status && customer.status === "blocked")
      ) {
        Swal.fire({
          icon: "warning",
          title: "Atenção!",
          text: "Seu cadastro está inativo. Entre em contato com a loja.",
        });
        setLoading(false);
        return;
      }

      if (!skipBirthDate) {
        if (
          !customer.birthDate ||
          !sameBirthDate(customer.birthDate, birthDate)
        ) {
          Swal.fire({
            icon: "warning",
            title: "Atenção!",
            text: "CPF ou data de nascimento inválidos.",
          });
          setLoading(false);
          return;
        }
      }

      setCpf(documentValue);
      setCustomerId(Number(customer.id));
      setCustomerName(customer.name || "");
      await loadStatement(Number(customer.id));
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível validar o cliente.",
      });
      setLoading(false);
    } finally {
      setIdentifying(false);
    }
  }

  function resetIdentity() {
    setCustomerId(null);
    setCustomerName("");
    setTransactions([]);
    setAvailableBalance(0);
    setPendingBalance(0);
    setTotalBalance(0);
    setBirthDate("");
    if (!loginInfo?.identifier) {
      setCpf("");
    }
  }

  const showFullLoading =
    loadingHeader ||
    (loading && !isIdentified && !!loginInfo?.identifier) ||
    (loading && isIdentified);

  return (
    <div className="cashback-statement">
      <div className="content-cashback-statement">
        {showFullLoading ? (
          <div
            className="cashback-loading-full"
            style={{ width: "100%", height: "100vh", minHeight: "100vh" }}
          >
            <Loading
              type="spinningBubbles"
              color="#b5b5b5"
              width={64}
              height={64}
            />
          </div>
        ) : (
          <>
            <Header
              background={
                config?.webappcolor?.backgroundheader
                  ? config.webappcolor.backgroundheader
                  : "#F5F5F5"
              }
              colorIcon={
                config?.webappcolor?.iconsheader
                  ? config.webappcolor.iconsheader
                  : "#636363"
              }
              logo={config?.icon ? config.icon : ""}
              backgroundCart={
                config?.webappcolor?.backgroundcart
                  ? config.webappcolor.backgroundcart
                  : "#FFBA49"
              }
              fontCart={
                config?.webappcolor?.fontcart
                  ? config.webappcolor.fontcart
                  : "#000"
              }
              noSales={
                config?.content?.pdv?.noSales
                  ? config.content.pdv.noSales
                  : false
              }
            />

            <div className="cashback-titlebar">
              <h2>Meu Cashback</h2>
              <svg
                onClick={() => navigate(-1)}
                width="18"
                height="17"
                viewBox="0 0 18 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0.978551 2.21657L6.957 8.2395L0.978551 14.2624L2.97137 16.2701L8.94981 10.2471L14.9283 16.2701L16.9211 14.2624L10.9426 8.2395L16.9211 2.21657L14.9283 0.208931L8.94981 6.23186L2.97137 0.208931L0.978551 2.21657Z"
                  fill="#636363"
                />
              </svg>
            </div>

            <div className="cashback-body">
              {!isIdentified ? (
                <>
                  <p className="cashback-intro">
                    Informe o CPF e a data de nascimento cadastrados para
                    consultar seu extrato de cashback.
                  </p>
                  <div className="cashback-inputs">
                    <InputMask
                      mask="999.999.999-99"
                      type="text"
                      value={cpf}
                      placeholder="Número do CPF"
                      onChange={(event) => setCpf(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          void identifyByDocument(cpf);
                        }
                      }}
                    />
                    <InputMask
                      mask="99/99/9999"
                      type="text"
                      value={birthDate}
                      placeholder="Data de nascimento"
                      onChange={(event) => setBirthDate(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          void identifyByDocument(cpf);
                        }
                      }}
                    />
                  </div>
                  <div className="cashback-button">
                    <MainButton
                      color={accentFont}
                      backgroundColor={accent}
                      text="Consultar"
                      onClick={() => void identifyByDocument(cpf)}
                      loaderIcon={identifying}
                      disabled={identifying}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="cashback-greeting">
                    <p>
                      {customerName ? (
                        <>
                          Olá, <strong>{customerName}</strong>
                        </>
                      ) : (
                        "Seu extrato de cashback"
                      )}
                    </p>
                    {!loginInfo?.identifier ? (
                      <button
                        type="button"
                        className="cashback-change-user"
                        onClick={resetIdentity}
                      >
                        Outro CPF
                      </button>
                    ) : null}
                  </div>

                  <div className="cashback-wallet">
                    <div className="cashback-wallet-total-block">
                      <p className="cashback-wallet-label">Saldo total</p>
                      <h3 className="cashback-wallet-total">
                        {floatToPriceWithoutSpace(totalBalance)}
                      </h3>
                    </div>
                    <div className="cashback-wallet-grid">
                      <div className="cashback-wallet-box available">
                        <span>Disponível</span>
                        <strong>
                          {floatToPriceWithoutSpace(availableBalance)}
                        </strong>
                      </div>
                      <div className="cashback-wallet-box pending">
                        <span>Pendente</span>
                        <strong>
                          {floatToPriceWithoutSpace(pendingBalance)}
                        </strong>
                      </div>
                    </div>
                  </div>

                  <h4 className="cashback-section-title">Movimentações</h4>

                  <div className="cashback-list">
                    {transactions.length === 0 ? (
                      <p className="cashback-empty">
                        Nenhuma movimentação de cashback encontrada.
                      </p>
                    ) : (
                      transactions.map((item) => {
                        const type = (item.type ||
                          "earn") as CashbackTransactionType;
                        const amount = Number(item.amount) || 0;
                        return (
                          <div className="cashback-item" key={item.id}>
                            <div className="cashback-item-info">
                              <div className="cashback-item-top">
                                <span className="cashback-type-badge">
                                  {TYPE_LABEL[type] || type}
                                </span>
                              </div>
                              <span className="cashback-meta">
                                {formatTransactionDate(item.created)}
                                {item.orderId
                                  ? ` · Pedido #${item.orderId}`
                                  : ""}
                              </span>
                              {item.description ? (
                                <span className="cashback-desc">
                                  {item.description}
                                </span>
                              ) : null}
                            </div>
                            <div className="cashback-item-amount">
                              {formatSignedAmount(amount)}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {!loadingHeader && !showFullLoading && (
          <Menu
            activeMenu="order"
            fontColor={
              config?.webappcolor?.iconsfooter
                ? config.webappcolor.iconsfooter
                : "#636363"
            }
            activeFontColor={
              config?.webappcolor?.highlightfooter
                ? config.webappcolor.highlightfooter
                : "#000"
            }
            backgroundMenu={
              config?.webappcolor?.backgroundfooter
                ? config.webappcolor.backgroundfooter
                : "#F5F5F5"
            }
            colorIcons={
              config?.webappcolor?.iconsfooter
                ? config.webappcolor.iconsfooter
                : "#636363"
            }
            highlightColorIcons={
              config?.webappcolor?.highlightfooter
                ? config.webappcolor.highlightfooter
                : "#636363"
            }
            backgroundCart={
              config?.webappcolor?.backgroundcart
                ? config.webappcolor.backgroundcart
                : "#FFBA49"
            }
            fontCart={
              config?.webappcolor?.fontcart
                ? config.webappcolor.fontcart
                : "#000"
            }
            noSales={
              config?.content?.pdv?.noSales ? config.content.pdv.noSales : false
            }
          />
        )}
      </div>
    </div>
  );
}
